import {
  Application,
  Container,
  FederatedPointerEvent,
  Graphics,
  Rectangle,
  Text,
  type Ticker,
} from 'pixi.js';
import type { GhostEvent, GhostTrack, Meaning, PublicActor } from '@smysly/shared';
import type { WorldEffect } from './events.js';

interface EngineOptions {
  onMeaningTap(id: string): void;
  onActorTap(actor: PublicActor): void;
  onMove(x: number, y: number): void;
  onSpawn(meaning: Meaning): void;
}

interface Mote {
  view: Graphics;
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
  target?: MeaningView;
}

export class WorldEngine {
  private readonly app = new Application();
  private readonly background = new Container();
  private readonly world = new Container();
  private readonly effects = new Container();
  private readonly actorLayer = new Container();
  private readonly meaningLayer = new Container();
  private readonly meanings = new Map<string, MeaningView>();
  private readonly actors = new Map<string, ActorView>();
  private readonly motes: Mote[] = [];
  private readonly stars: Star[] = [];
  private avatar!: AvatarView;
  private width = 1;
  private height = 1;
  private playerX = 0;
  private playerY = 0;
  private targetX = 0;
  private targetY = 0;
  private time = 0;
  private reducedMotion = false;
  private destroyed = false;

  constructor(private readonly options: EngineOptions) {}

  async init(host: HTMLElement): Promise<void> {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const resolution = Math.min(window.devicePixelRatio || 1, navigator.hardwareConcurrency <= 4 ? 1.25 : 1.75);
    await this.app.init({
      resizeTo: host,
      backgroundAlpha: 0,
      antialias: true,
      resolution,
      autoDensity: true,
      preference: 'webgl',
    });
    if (this.destroyed) return;
    host.appendChild(this.app.canvas);
    this.app.canvas.setAttribute('aria-hidden', 'true');
    this.app.stage.addChild(this.background, this.world, this.effects);
    this.world.addChild(this.actorLayer, this.meaningLayer);
    this.avatar = new AvatarView(false, 0xf4dca3);
    this.world.addChild(this.avatar.container);
    this.createStars();
    this.app.stage.eventMode = 'static';
    this.app.stage.hitArea = new Rectangle(0, 0, host.clientWidth, host.clientHeight);
    this.app.stage.on('pointertap', (event) => this.moveFromPointer(event));
    this.app.ticker.add(this.tick);
    this.layout();
  }

  destroy(): void {
    this.destroyed = true;
    this.app.ticker.remove(this.tick);
    this.app.destroy({ removeView: true }, { children: true });
  }

  syncMeanings(next: Meaning[]): void {
    const active = next.filter((meaning) => meaning.status !== 'popped' && meaning.status !== 'archived');
    const ids = new Set(active.map((meaning) => meaning.id));
    for (const [id, view] of this.meanings) {
      if (!ids.has(id)) {
        view.destroy();
        this.meanings.delete(id);
      }
    }
    for (const meaning of active) {
      let view = this.meanings.get(meaning.id);
      if (!view) {
        view = new MeaningView(meaning, (id) => this.options.onMeaningTap(id));
        this.meanings.set(meaning.id, view);
        this.meaningLayer.addChild(view.container);
        this.spawnAt(view);
        this.options.onSpawn(meaning);
      } else {
        view.sync(meaning);
      }
    }
  }

  syncActors(next: PublicActor[]): void {
    const ids = new Set(next.map((actor) => actor.publicId));
    for (const [id, view] of this.actors) {
      if (!ids.has(id)) {
        view.fadeOut();
        window.setTimeout(() => { view.destroy(); this.actors.delete(id); }, 700);
      }
    }
    for (const actor of next) {
      const existing = this.actors.get(actor.publicId);
      if (existing) existing.sync(actor);
      else {
        const view = new ActorView(actor, (selected) => this.options.onActorTap(selected));
        this.actors.set(actor.publicId, view);
        this.actorLayer.addChild(view.container);
      }
    }
  }

  syncGhostTracks(tracks: GhostTrack[]): void {
    for (const track of tracks) this.actors.get(track.publicId)?.setTrack(track.events);
  }

  effect(effect: WorldEffect): void {
    if (effect.type === 'pop' || effect.type === 'shell-pop') {
      const view = this.meanings.get(effect.id);
      if (!view) return;
      const point = view.worldPoint();
      if (effect.type === 'shell-pop') view.crackShell();
      else view.pop();
      this.burst(point.x, point.y, effect.type === 'shell-pop' ? 24 : 30, effect.type === 'shell-pop' ? 0xb8d8ff : 0xf6d99b);
    } else if (effect.type === 'energy') {
      const target = this.meanings.get(effect.id);
      if (target) this.energyTransfer(target, effect.amount);
    } else if (effect.type === 'selection') {
      this.burst(this.width * 0.5, this.height * 0.43, 16, 0xf6d99b);
    }
  }

  private readonly tick = (ticker: Ticker): void => {
    const dt = Math.min(0.04, ticker.deltaMS / 1000);
    this.time += dt;
    if (this.width !== this.app.screen.width || this.height !== this.app.screen.height) this.layout();
    const smooth = 1 - Math.exp(-dt * 4.5);
    this.playerX += (this.targetX - this.playerX) * smooth;
    this.playerY += (this.targetY - this.playerY) * smooth;
    const centerX = this.width * 0.5 + this.playerX * this.width * 0.25;
    const centerY = this.height * 0.43 + this.playerY * this.height * 0.2;
    this.avatar.update(centerX, centerY, this.time, dt);

    let index = 0;
    for (const view of this.meanings.values()) {
      const angle = index * 2.39996 + this.time * (0.035 + index * 0.002);
      const ring = 94 + (index % 3) * 50 + view.radius * 0.5;
      const x = centerX + Math.cos(angle) * Math.min(ring, this.width * 0.34);
      const y = centerY + Math.sin(angle) * Math.min(ring * 0.72, this.height * 0.2);
      view.update(x, y, this.time, dt, this.reducedMotion);
      index += 1;
    }
    for (const actor of this.actors.values()) actor.update(this.width, this.height, this.time, dt, this.reducedMotion);
    for (const star of this.stars) star.update(this.width, this.height, this.time, dt);
    this.updateMotes(dt);
  };

  private layout(): void {
    this.width = this.app.screen.width;
    this.height = this.app.screen.height;
    this.app.stage.hitArea = new Rectangle(0, 0, this.width, this.height);
  }

  private moveFromPointer(event: FederatedPointerEvent): void {
    if (event.target !== this.app.stage) return;
    this.targetX = clamp((event.global.x / this.width - 0.5) * 2, -0.9, 0.9);
    this.targetY = clamp((event.global.y / this.height - 0.43) * 2, -0.8, 0.58);
    this.options.onMove(this.targetX, this.targetY);
  }

  private createStars(): void {
    const count = navigator.hardwareConcurrency <= 4 ? 44 : 76;
    for (let index = 0; index < count; index += 1) {
      const star = new Star(index);
      this.stars.push(star);
      this.background.addChild(star.view);
    }
  }

  private spawnAt(view: MeaningView): void {
    const x = this.width * 0.5;
    const y = this.height * 0.43;
    for (let index = 0; index < 12; index += 1) {
      const angle = index / 12 * Math.PI * 2;
      this.addMote(x + Math.cos(angle) * 48, y + Math.sin(angle) * 32, -Math.cos(angle) * 30, -Math.sin(angle) * 30, 0.7, 0xf6d99b, view);
    }
  }

  private burst(x: number, y: number, count: number, color: number): void {
    const qualityCount = navigator.hardwareConcurrency <= 4 ? Math.ceil(count * 0.55) : count;
    for (let index = 0; index < qualityCount; index += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 46 + Math.random() * 115;
      this.addMote(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, 1.5 + Math.random(), color);
    }
  }

  private energyTransfer(target: MeaningView, amount: number): void {
    const count = Math.min(24, 6 + amount * 2);
    for (let index = 0; index < count; index += 1) {
      this.addMote(this.width * 0.5 + (Math.random() - 0.5) * 100, this.height - 92, 0, -20, 0.9 + Math.random() * 0.4, 0xf6d99b, target);
    }
    target.pulse();
  }

  private addMote(x: number, y: number, vx: number, vy: number, life: number, color: number, target?: MeaningView): void {
    const view = new Graphics().circle(0, 0, 1.2 + Math.random() * 2.4).fill({ color, alpha: 0.9 });
    view.position.set(x, y);
    this.effects.addChild(view);
    this.motes.push({ view, x, y, vx, vy, age: 0, life, ...(target ? { target } : {}) });
  }

  private updateMotes(dt: number): void {
    for (let index = this.motes.length - 1; index >= 0; index -= 1) {
      const mote = this.motes[index]!;
      mote.age += dt;
      if (mote.target) {
        const target = mote.target.worldPoint();
        const force = Math.min(12, mote.age * 18);
        mote.vx += (target.x - mote.x) * force * dt;
        mote.vy += (target.y - mote.y) * force * dt;
      } else {
        mote.vx *= Math.pow(0.35, dt);
        mote.vy *= Math.pow(0.35, dt);
        mote.vy -= 4 * dt;
      }
      mote.x += mote.vx * dt;
      mote.y += mote.vy * dt;
      mote.view.position.set(mote.x, mote.y);
      mote.view.alpha = Math.max(0, 1 - mote.age / mote.life);
      if (mote.age >= mote.life) {
        mote.view.destroy();
        this.motes.splice(index, 1);
      }
    }
  }
}

class MeaningView {
  readonly container = new Container();
  readonly body = new Graphics();
  readonly label: Text;
  radius = 20;
  private targetRadius = 20;
  private spawn = 0;
  private shellSpawn = 0;
  private popProgress = 0;
  private pulseValue = 0;
  private meaning: Meaning;
  private x = 0;
  private y = 0;

  constructor(meaning: Meaning, onTap: (id: string) => void) {
    this.meaning = meaning;
    this.targetRadius = radiusFor(meaning.importance);
    this.label = new Text({
      text: meaning.title,
      style: { fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, fill: 0xf5f0e6, fontWeight: '500', align: 'center', dropShadow: { color: 0x05070d, alpha: 0.8, blur: 3, distance: 1 } },
    });
    this.label.anchor.set(0.5, 0);
    this.container.addChild(this.body, this.label);
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';
    this.container.on('pointertap', (event: FederatedPointerEvent) => { event.stopPropagation(); onTap(this.meaning.id); this.pulse(); });
  }

  sync(meaning: Meaning): void {
    const hadShell = Boolean(this.meaning.shell && this.meaning.shell.status !== 'removed');
    this.meaning = meaning;
    this.targetRadius = radiusFor(meaning.importance);
    this.label.text = meaning.title;
    if (!hadShell && meaning.shell && meaning.shell.status !== 'removed') this.shellSpawn = 0.02;
  }

  update(x: number, y: number, time: number, dt: number, reduced: boolean): void {
    this.x += (x - this.x) * (1 - Math.exp(-dt * 3.8));
    this.y += (y - this.y) * (1 - Math.exp(-dt * 3.8));
    this.spawn = Math.min(1, this.spawn + dt * 1.65);
    this.radius += (this.targetRadius - this.radius) * (1 - Math.exp(-dt * 5));
    this.pulseValue *= Math.pow(0.012, dt);
    if (this.meaning.shell && this.meaning.shell.status !== 'removed') this.shellSpawn = Math.min(1, this.shellSpawn + dt * 1.2);
    const easedSpawn = easeOutBack(this.spawn);
    const breath = reduced ? 1 : 1 + Math.sin(time * (1.1 + this.meaning.vitality / 110)) * 0.024;
    const popScale = this.popProgress > 0 ? Math.max(0, 1 - this.popProgress * 1.35) : 1;
    if (this.popProgress > 0) this.popProgress += dt * 2.4;
    this.container.position.set(this.x, this.y + (reduced ? 0 : Math.sin(time * 0.7 + this.meaning.x * 9) * 4));
    this.container.scale.set(easedSpawn * breath * (1 + this.pulseValue * 0.12) * popScale, easedSpawn / breath * (1 - this.pulseValue * 0.05) * popScale);
    this.container.alpha = Math.max(0, 1 - Math.max(0, this.popProgress - 0.2));
    this.draw(time);
  }

  worldPoint(): { x: number; y: number } { return { x: this.container.x, y: this.container.y }; }
  pulse(): void { this.pulseValue = 1; }
  pop(): void { this.popProgress = 0.08; }
  crackShell(): void { this.shellSpawn = -0.6; }
  destroy(): void { this.container.destroy({ children: true }); }

  private draw(time: number): void {
    const radius = this.radius;
    const energyRadius = radius * (0.35 + this.meaning.realization / 165);
    const vitalityAlpha = 0.18 + this.meaning.vitality / 360;
    this.body.clear();
    this.body.circle(0, 0, radius * 1.42).fill({ color: 0x7a91d4, alpha: 0.025 + vitalityAlpha * 0.08 });
    this.body.circle(0, 0, radius).fill({ color: 0x17233c, alpha: 0.72 }).stroke({ color: 0xd8c691, width: 1.1, alpha: 0.58 });
    this.body.circle(-radius * 0.16, -radius * 0.16, energyRadius).fill({ color: 0xf1d99a, alpha: vitalityAlpha });
    this.body.circle(-radius * 0.24, -radius * 0.28, radius * 0.17).fill({ color: 0xffffff, alpha: 0.14 });
    for (let index = 0; index < 5; index += 1) {
      const angle = time * (0.2 + index * 0.016) + index * 1.9;
      const distance = radius * (0.22 + (index % 3) * 0.17);
      this.body.circle(Math.cos(angle) * distance, Math.sin(angle) * distance, 0.8 + index % 2).fill({ color: 0xffebbd, alpha: 0.35 + this.meaning.realization / 240 });
    }
    if (this.meaning.shell && this.meaning.shell.status !== 'removed') {
      const shellRadius = radius + 8 * easeOutCubic(Math.max(0, this.shellSpawn));
      const shellAlpha = this.shellSpawn < 0 ? Math.max(0, 0.45 + this.shellSpawn) : Math.min(0.45, this.shellSpawn * 0.45);
      for (let index = 0; index < 5; index += 1) {
        const start = index * 1.19 + time * 0.045;
        this.body.arc(0, 0, shellRadius, start, start + 0.86).stroke({ color: 0xaec6ff, width: 1.2 + this.meaning.shell.strength / 80, alpha: shellAlpha });
      }
    }
    this.label.y = radius + 9;
    this.label.alpha = this.spawn > 0.72 ? Math.min(0.88, (this.spawn - 0.72) * 3.6) : 0;
  }
}

class AvatarView {
  readonly container = new Container();
  private readonly aura = new Graphics();
  private readonly body = new Graphics();
  private x = 0;
  private y = 0;

  constructor(private readonly ghost: boolean, private readonly color: number) {
    this.container.addChild(this.aura, this.body);
    this.container.alpha = ghost ? 0.38 : 1;
  }

  update(x: number, y: number, time: number, dt: number): void {
    this.x += (x - this.x) * (1 - Math.exp(-dt * 5));
    this.y += (y - this.y) * (1 - Math.exp(-dt * 5));
    this.container.position.set(this.x, this.y);
    const breathe = 1 + Math.sin(time * 1.4) * 0.06;
    this.aura.clear().circle(0, 0, 36 * breathe).fill({ color: this.color, alpha: this.ghost ? 0.045 : 0.07 });
    this.body.clear();
    this.body.ellipse(0, -2, 7.5, 12.5 * breathe).fill({ color: this.color, alpha: this.ghost ? 0.42 : 0.92 });
    this.body.circle(0, -14, 4.8).fill({ color: this.color, alpha: this.ghost ? 0.52 : 1 });
    this.body.circle(0, -6, 15).fill({ color: this.color, alpha: this.ghost ? 0.065 : 0.11 });
  }
}

class ActorView {
  readonly container = new Container();
  private readonly avatar: AvatarView;
  private readonly bubbles: Graphics[] = [];
  private actor: PublicActor;
  private x = 0;
  private y = 0;
  private fade = 1;
  private track: GhostEvent[] = [];
  private lastTrackKey = '';
  private readonly color: number;

  constructor(actor: PublicActor, onTap: (actor: PublicActor) => void) {
    this.actor = actor;
    this.color = colorForActor(actor.publicId, actor.kind === 'ghost');
    this.avatar = new AvatarView(actor.kind === 'ghost', this.color);
    this.container.addChild(this.avatar.container);
    this.container.eventMode = 'static';
    this.container.cursor = 'pointer';
    this.container.on('pointertap', (event: FederatedPointerEvent) => { event.stopPropagation(); onTap(this.actor); });
    this.rebuildBubbles();
  }

  sync(actor: PublicActor): void {
    const previousKey = this.actor.meanings.map((meaning) => `${meaning.id}:${meaning.importance}`).join('|');
    const nextKey = actor.meanings.map((meaning) => `${meaning.id}:${meaning.importance}`).join('|');
    this.actor = actor;
    if (previousKey !== nextKey) this.rebuildBubbles();
  }

  setTrack(events: GhostEvent[]): void {
    this.track = events;
    this.lastTrackKey = '';
  }

  update(width: number, height: number, time: number, dt: number, reduced: boolean): void {
    if (this.track.length) this.applyTrack(time);
    const drift = this.actor.kind === 'ghost' && !reduced ? Math.sin(time * 0.22 + this.actor.x * 8) * 0.06 : 0;
    const targetX = width * (0.5 + (this.actor.x + drift) * 0.43);
    const targetY = height * (0.42 + this.actor.y * 0.3);
    this.x += (targetX - this.x) * (1 - Math.exp(-dt * 2));
    this.y += (targetY - this.y) * (1 - Math.exp(-dt * 2));
    this.container.position.set(this.x, this.y);
    this.container.alpha = this.fade * (this.actor.kind === 'ghost' ? 0.48 + Math.sin(time * 2.3 + this.actor.y) * 0.06 : 0.72);
    this.avatar.update(0, 0, time + this.actor.x * 6, dt);
    this.bubbles.forEach((bubble, index) => {
      const angle = time * 0.08 + index * 2.1;
      bubble.position.set(Math.cos(angle) * (24 + index * 6), Math.sin(angle) * (17 + index * 4) - 6);
    });
  }

  fadeOut(): void { this.fade = 0; }
  destroy(): void { this.container.destroy({ children: true }); }

  private applyTrack(time: number): void {
    const duration = Math.max(20, (this.track.at(-1)?.t ?? 20) + 4);
    const playback = time % duration;
    const completed = this.track.filter((event) => event.t <= playback);
    const moves = this.track.filter((event): event is Extract<GhostEvent, { event: 'MOVE' }> => event.event === 'MOVE');
    const previousMove = [...moves].reverse().find((event) => event.t <= playback) ?? moves[0];
    const nextMove = moves.find((event) => event.t > playback) ?? previousMove;
    if (previousMove && nextMove) {
      const span = Math.max(.01, nextMove.t - previousMove.t);
      const mix = Math.min(1, Math.max(0, (playback - previousMove.t) / span));
      this.actor.x = previousMove.x + (nextMove.x - previousMove.x) * mix;
      this.actor.y = previousMove.y + (nextMove.y - previousMove.y) * mix;
    }
    const bubbles = new Map<string, { id: string; publicCategory: string | null; importance: number }>();
    for (const event of completed) {
      if (event.event === 'CREATE_BUBBLE') bubbles.set(event.bubbleId, { id: event.bubbleId, publicCategory: event.publicCategory, importance: 18 });
      else if (event.event === 'RESIZE_BUBBLE') {
        const bubble = bubbles.get(event.bubbleId);
        if (bubble) bubble.importance = event.value;
      } else if (event.event === 'POP_BUBBLE') bubbles.delete(event.bubbleId);
    }
    const key = [...bubbles.values()].map((bubble) => `${bubble.id}:${bubble.importance}`).join('|');
    if (key !== this.lastTrackKey) {
      this.lastTrackKey = key;
      this.actor.meanings = [...bubbles.values()];
      this.rebuildBubbles();
    }
  }

  private rebuildBubbles(): void {
    for (const bubble of this.bubbles) bubble.destroy();
    this.bubbles.length = 0;
    for (const meaning of this.actor.meanings.slice(0, 5)) {
      const radius = 3.5 + Math.sqrt(meaning.importance) * 0.8;
      const bubble = new Graphics().circle(0, 0, radius).fill({ color: this.color, alpha: this.actor.kind === 'ghost' ? 0.16 : 0.3 }).stroke({ color: this.color, width: 0.7, alpha: 0.6 });
      this.bubbles.push(bubble);
      this.container.addChild(bubble);
    }
  }
}

class Star {
  readonly view = new Graphics();
  private readonly nx: number;
  private readonly ny: number;
  private readonly size: number;
  private readonly speed: number;

  constructor(seed: number) {
    this.nx = seeded(seed * 3 + 1);
    this.ny = seeded(seed * 7 + 9);
    this.size = 0.3 + seeded(seed * 11 + 4) * 1.35;
    this.speed = 0.12 + seeded(seed * 5 + 8) * 0.35;
    this.view.circle(0, 0, this.size).fill({ color: seed % 7 === 0 ? 0xc4b78e : 0xabc5ee, alpha: 0.18 + seeded(seed) * 0.34 });
  }

  update(width: number, height: number, time: number, _dt: number): void {
    this.view.position.set(this.nx * width + Math.sin(time * this.speed + this.ny * 8) * 4, this.ny * height + Math.cos(time * this.speed + this.nx * 9) * 3);
  }
}

function radiusFor(importance: number): number { return 15 + Math.sqrt(Math.max(1, importance)) * 3.25; }
function clamp(value: number, min: number, max: number): number { return Math.min(max, Math.max(min, value)); }
function easeOutCubic(value: number): number { return 1 - Math.pow(1 - Math.min(1, value), 3); }
function easeOutBack(value: number): number {
  const x = Math.min(1, value) - 1;
  return 1 + 2.70158 * x * x * x + 1.70158 * x * x;
}
function seeded(value: number): number { return Math.abs(Math.sin(value * 12.9898) * 43758.5453) % 1; }
function colorForActor(id: string, ghost: boolean): number {
  const palette = ghost ? [0x7897c9, 0x8d82b8, 0x6d9e99] : [0xf08fa6, 0x79c7ef, 0x8ad7c1, 0xd69cf0, 0xefb36e, 0x9bb2ff, 0xe6ca7d];
  let hash = 0;
  for (const character of id) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return palette[hash % palette.length]!;
}
